import { useNavigate } from "react-router-dom";
import { Heart, Users, Sparkles, Music, Code, Star, MessageCircle, UserPlus } from "lucide-react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useAuth } from "@/App";
import { useEffect } from "react";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const features = [
    { icon: Code, title: "Customize Profile", desc: "HTML & CSS - make it YOURS!", color: "#FF00FF" },
    { icon: Music, title: "Profile Songs", desc: "Show off your music taste", color: "#00FFFF" },
    { icon: Users, title: "Top 8 Friends", desc: "Display your besties", color: "#FFFF00" },
    { icon: MessageCircle, title: "Comments", desc: "Leave comments on profiles", color: "#00FF00" },
    { icon: Heart, title: "Mood Status", desc: "Let everyone know how you feel", color: "#FF6600" },
    { icon: Star, title: "Bulletins", desc: "Post updates for all friends", color: "#FF00FF" },
  ];

  return (
    <div className="min-h-screen relative z-10">
      {/* Glittery Marquee */}
      <div className="myspace-marquee">
        <Marquee speed={60} gradient={false}>
          <span className="text-[#FF00FF] font-bold mx-6">★ WELCOME TO OURSPACEZ ★</span>
          <span className="text-[#00FFFF] font-bold mx-6">✦ A PLACE FOR FRIENDS ✦</span>
          <span className="text-[#FFFF00] font-bold mx-6">★ CUSTOMIZE YOUR WORLD ★</span>
          <span className="text-[#00FF00] font-bold mx-6">✦ EXPRESS YOURSELF ✦</span>
          <span className="text-[#FF6600] font-bold mx-6">★ CONNECT WITH FRIENDS ★</span>
        </Marquee>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto"
        >
          {/* Logo */}
          <motion.h1 
            className="text-6xl sm:text-7xl lg:text-8xl font-black mb-4"
            animate={{ textShadow: ["0 0 10px #FF00FF", "0 0 20px #00FFFF", "0 0 10px #FF00FF"] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <span className="glitter-text">Our</span>
            <span className="text-[#00FFFF]" style={{ textShadow: '0 0 20px #00FFFF' }}>Spacez</span>
          </motion.h1>
          
          <p className="text-xl text-[#99CCFF] mb-2">a place for friends</p>
          
          <motion.p 
            className="text-[#FFFF00] text-sm mb-8"
            animate={{ opacity: [1, 0.5, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            ★ Now with MORE customization! ★
          </motion.p>

          {/* Auth Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 30px #00FFFF" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/register")}
              className="myspace-btn-primary text-lg px-8 py-3"
              data-testid="get-started-btn"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Sign Up FREE!
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 30px #FF00FF" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/login")}
              className="myspace-btn text-lg px-8 py-3"
              data-testid="login-btn"
            >
              Member Login
            </motion.button>
          </div>

          {/* Profile Preview Cards */}
          <div className="flex justify-center gap-4 mb-12 flex-wrap">
            {[
              { name: "★ R.C ★", mood: "chillin", online: true, color: "#FF00FF" },
              { name: "xXReceeXx", mood: "excited!", online: true, color: "#00FFFF" },
              { name: "~*Byeron*~", mood: "rockin out", online: false, color: "#FFFF00" },
            ].map((profile, i) => (
              <motion.div
                key={profile.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.1 }}
                whileHover={{ scale: 1.05, rotate: i % 2 === 0 ? 2 : -2 }}
                className="myspace-card w-40 text-center"
                style={{ borderColor: profile.color }}
              >
                <div 
                  className="w-20 h-20 mx-auto mb-2 border-2 flex items-center justify-center text-3xl"
                  style={{ borderColor: profile.color, background: 'rgba(0,0,0,0.5)' }}
                >
                  {i === 0 ? "😎" : i === 1 ? "💖" : "🎸"}
                </div>
                <p className="font-bold text-sm" style={{ color: profile.color }}>{profile.name}</p>
                <p className="text-xs text-[#99CCFF]">Mood: {profile.mood}</p>
                {profile.online && (
                  <div className="flex items-center justify-center gap-1 mt-1">
                    <span className="online-dot w-2 h-2"></span>
                    <span className="text-[#00FF00] text-xs">Online Now!</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Features Section */}
        <div className="myspace-card max-w-4xl mx-auto mb-8">
          <h2 className="section-header text-center text-lg mb-6">
            ★ Why Join OurSpacez? ★
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="text-center p-4 border border-[#6600CC] bg-[rgba(0,0,51,0.5)]"
              >
                <feature.icon 
                  className="w-8 h-8 mx-auto mb-2" 
                  style={{ color: feature.color, filter: `drop-shadow(0 0 5px ${feature.color})` }}
                />
                <h3 className="font-bold text-sm mb-1" style={{ color: feature.color }}>
                  {feature.title}
                </h3>
                <p className="text-xs text-[#99CCFF]">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Stats Counters */}
        <div className="flex justify-center gap-8 mb-8 flex-wrap">
          <div className="text-center">
            <div className="view-counter text-2xl px-4 py-2">
              1,337,420
            </div>
            <p className="text-xs text-[#99CCFF] mt-1">Members Online</p>
          </div>
          <div className="text-center">
            <div className="view-counter text-2xl px-4 py-2">
              42,069,000
            </div>
            <p className="text-xs text-[#99CCFF] mt-1">Total Profiles</p>
          </div>
        </div>

        {/* Cool Stuff Section */}
        <div className="max-w-2xl mx-auto text-center mb-8">
          <motion.div
            className="myspace-card"
            style={{ borderColor: '#FFFF00' }}
            animate={{ boxShadow: ["0 0 10px #FFFF00", "0 0 20px #FF00FF", "0 0 10px #FFFF00"] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <h3 className="text-[#FFFF00] font-bold mb-2" style={{ textShadow: '0 0 10px #FFFF00' }}>
              ★ COOL NEW FEATURES ★
            </h3>
            <ul className="text-sm text-[#CCCCFF] space-y-1">
              <li>✦ Custom HTML/CSS profiles - just like the old days!</li>
              <li>✦ Add your favorite song to your profile</li>
              <li>✦ Top 8 Friends display</li>
              <li>✦ Mood status updates</li>
              <li>✦ Profile comments & guestbook</li>
              <li>✦ Glitter text & sparkly backgrounds!</li>
            </ul>
          </motion.div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/register")}
            className="px-12 py-4 text-xl font-bold"
            style={{
              background: 'linear-gradient(180deg, #FF00FF 0%, #660066 100%)',
              border: '3px solid #FF00FF',
              color: 'white',
              boxShadow: '0 0 20px #FF00FF',
              textShadow: '0 0 10px white'
            }}
            animate={{ 
              boxShadow: ["0 0 20px #FF00FF", "0 0 40px #00FFFF", "0 0 20px #FF00FF"]
            }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            ★ JOIN OURSPACEZ NOW! ★
          </motion.button>
          <p className="text-[#99CCFF] text-sm mt-4">
            Free to join · Creator plans unlock the good stuff
          </p>
          <a
            href="/plans"
            onClick={(e) => { e.preventDefault(); navigate("/plans"); }}
            className="inline-block mt-3 text-[#FF66FF] text-xs underline hover:text-[#00FFFF] font-bold tracking-wide"
            data-testid="pricing-link"
          >
            See Pricing →
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t-2 border-[#6600CC] py-6 mt-12 bg-[rgba(0,0,51,0.8)]">
        <div className="container mx-auto px-4 text-center">
          <p className="text-2xl font-bold mb-2">
            <span className="glitter-text">Our</span>
            <span className="text-[#00FFFF]">Space</span>
          </p>
          <p className="text-[#99CCFF] text-sm">a place for friends</p>
          <div className="flex justify-center gap-2 mt-2 text-xl">
            <span>💖</span>
            <span>✨</span>
            <span>🎵</span>
            <span>⭐</span>
            <span>💜</span>
          </div>
          <p className="text-[#6699CC] text-xs mt-4">
            © 2026 OurSpacez | <a href="#" className="text-[#FF66FF]">Terms</a> | <a href="#" className="text-[#FF66FF]">Privacy</a> | <a href="#" className="text-[#FF66FF]">Safety</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
